// The links on a record: several of them, each with an optional name.
//
// One component, used by work and by deals, on the detail page and in the peek panel.
// The alternative was a `resource` record type, which crum had and removed: a link has
// no state, no date and no identity worth folding, so it's a repeatable FIELD — one line
// per link, `[final signed copy](https://…)` when you name it and a bare URL when you
// don't. Markdown because it reads as a link in the raw file, survives being edited by
// hand or by an agent, and matches the @mention token's shape.
//
// The label matters more than it looks: "draft_v2" and "counter-signed" are the
// difference between a shelf you can use and three near-identical Drive URLs.

import { useState } from 'react'

import { linkLabel, linksOf, linksToText, type RecordLink } from '../lib/model'
import { Icon } from './Icon'

const href = (url: string) => (/^[a-z]+:\/\//i.test(url) ? url : `https://${url}`)

/** Read-only: the list as chips-on-lines, each on ONE line, truncated not wrapped. */
export function LinkList({
  value,
  max = 3,
  size = 11.5,
}: {
  value: string
  max?: number
  size?: number
}) {
  const links = linksOf({ links: value })
  if (links.length === 0) return <span style={{ color: 'var(--faint)' }}>·</span>
  return (
    <span className="flex min-w-0 flex-col gap-0.5">
      {links.slice(0, max).map((l, i) => (
        <a
          key={`${l.url}-${i}`}
          href={href(l.url)}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="max-w-full min-w-0 truncate whitespace-nowrap hover:underline"
          style={{
            color: 'var(--marine)',
            fontFamily: l.label ? 'inherit' : 'var(--mono)',
            fontSize: size,
          }}
          title={l.label ? `${l.label} · ${l.url}` : l.url}
        >
          {linkLabel(l, 34)}
        </a>
      ))}
      {links.length > max && <span className="meta">+{links.length - max} more</span>}
    </span>
  )
}

/** The first link only, for a table cell: one line, never wrapping, with a +n. */
export function LinkCell({ value }: { value: string }) {
  const links = linksOf({ links: value })
  const first = links[0]
  if (!first) return <span style={{ color: 'var(--faint)' }}>·</span>
  return (
    // min-w-0 on BOTH the row and the anchor: without it the flex child takes its
    // content's width and the URL wraps at a slash instead of truncating.
    <span className="flex min-w-0 items-center gap-1.5">
      <Icon name="Link" size={12} className="shrink-0" style={{ color: 'var(--faint)' }} />
      <a
        href={href(first.url)}
        target="_blank"
        rel="noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="min-w-0 truncate whitespace-nowrap hover:underline"
        style={{
          color: 'var(--marine)',
          fontFamily: first.label ? 'inherit' : 'var(--mono)',
          fontSize: 'var(--text-label)',
        }}
        title={links.map((l) => (l.label ? `${l.label} · ${l.url}` : l.url)).join('\n')}
      >
        {linkLabel(first, 26)}
      </a>
      {links.length > 1 && <span className="meta shrink-0">+{links.length - 1}</span>}
    </span>
  )
}

/**
 * Editable: a name and a URL per row, plus one empty row to add the next.
 *
 * **What you type lives here until you leave the row.** Writing on every keystroke
 * looked simpler and was wrong twice over. The text made a round trip out through
 * `linksToText` and back through `linksOf` between one character and the next, and
 * both of those trim, so a trailing space was deleted as fast as it was typed and a
 * name of more than one word could not be entered at all. And every character was
 * its own `*.update` event, so naming one link wrote twenty rows into a log that is
 * append-only and therefore never forgets them.
 *
 * `editing` is the index of the row being typed in, or -1, and `row` is its text.
 * Two things keep that buffer honest: moving between a row's two inputs is not
 * leaving the row, and the buttons beside it take no focus, so nothing commits
 * behind their backs and every write is composed from `current()`.
 */
export function LinksField({ value, onSave }: { value: string; onSave: (v: string) => void }) {
  const links = linksOf({ links: value })
  const [draft, setDraft] = useState<RecordLink>({ label: '', url: '' })
  const [editing, setEditing] = useState(-1)
  const [row, setRow] = useState<RecordLink>({ label: '', url: '' })

  /** The list as it stands, including whatever is half-typed in a row. */
  const current = (): RecordLink[] =>
    editing >= 0 && links[editing] ? links.map((l, j) => (j === editing ? row : l)) : links

  const write = (next: RecordLink[]) => onSave(linksToText(next))

  const focusRow = (i: number) => {
    // Tabbing from a row's name to its URL is not leaving it: re-reading the row
    // here would hand back the saved text and swallow what was just typed.
    if (editing === i) return
    commitRow()
    setEditing(i)
    setRow(links[i] ?? { label: '', url: '' })
  }

  /** Commit unless focus merely moved to the other input in the same row. */
  const blurRow = (e: React.FocusEvent<HTMLInputElement>) => {
    const rowEl = e.currentTarget.closest('[data-link-row]')
    if (rowEl && e.relatedTarget instanceof Node && rowEl.contains(e.relatedTarget)) return
    commitRow()
  }

  const commitRow = () => {
    if (editing < 0) return
    const before = links[editing]
    const at = editing
    setEditing(-1)
    if (!before || (before.label === row.label && before.url === row.url)) return
    // A row emptied of its URL is a row removed: there is nothing left to point at.
    write(row.url.trim() ? current() : links.filter((_, j) => j !== at))
  }

  /** Order is meaningful: the FIRST link is the one a list shows, so it's sortable. */
  const move = (i: number, delta: number) => {
    const j = i + delta
    const next = current()
    if (j < 0 || j >= next.length) return
    const a = next[i]
    const b = next[j]
    if (!a || !b) return
    next[i] = b
    next[j] = a
    setEditing(-1)
    write(next)
  }

  const remove = (i: number) => {
    const next = current().filter((_, j) => j !== i)
    setEditing(-1)
    write(next)
  }

  const commitDraft = () => {
    if (!draft.url.trim()) return
    write([...current(), draft])
    setDraft({ label: '', url: '' })
  }

  /** The buttons beside a row must not take focus, or pressing one commits the row
   *  first and then acts on a list that is one write out of date. */
  const keepFocus = (e: React.MouseEvent) => e.preventDefault()

  return (
    <div className="flex flex-col gap-1.5">
      {links.map((l, i) => (
        <span
          key={`${l.url}-${i}`}
          data-link-row=""
          className="group flex min-w-0 items-center gap-1.5"
        >
          <a
            href={href(l.url)}
            target="_blank"
            rel="noreferrer"
            title={i === 0 ? `${l.url}\n(first: this is the one lists show)` : l.url}
            className="shrink-0"
            style={{ color: i === 0 ? 'var(--marine)' : 'var(--faint)' }}
          >
            <Icon name="Link" size={12} />
          </a>
          <input
            value={editing === i ? row.label : l.label}
            onFocus={() => focusRow(i)}
            onChange={(e) => setRow({ ...row, label: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur()
              if (e.key === 'Escape') setEditing(-1)
            }}
            onBlur={blurRow}
            placeholder="name"
            className="text-body min-w-0 flex-[2] bg-transparent outline-none"
          />
          <input
            value={editing === i ? row.url : l.url}
            onFocus={() => focusRow(i)}
            onChange={(e) => setRow({ ...row, url: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur()
              if (e.key === 'Escape') setEditing(-1)
            }}
            onBlur={blurRow}
            placeholder="https://"
            className="min-w-0 flex-[3] truncate bg-transparent outline-none"
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 'var(--text-label)',
              color: 'var(--marine)',
            }}
          />
          {/* Reordering matters because the first link is the one lists show. Both slots
              are always rendered so the row doesn't jump as you move things. */}
          {links.length > 1 && (
            <span className="flex shrink-0 items-center opacity-0 transition-opacity group-hover:opacity-60">
              <button
                type="button"
                onMouseDown={keepFocus}
                onClick={() => move(i, -1)}
                disabled={i === 0}
                className="hover:!opacity-100 disabled:opacity-25"
                title="Move up"
                aria-label={`Move ${linkLabel(l)} up`}
              >
                <Icon name="ChevronUp" size={12} />
              </button>
              <button
                type="button"
                onMouseDown={keepFocus}
                onClick={() => move(i, 1)}
                disabled={i === links.length - 1}
                className="hover:!opacity-100 disabled:opacity-25"
                title="Move down"
                aria-label={`Move ${linkLabel(l)} down`}
              >
                <Icon name="ChevronDown" size={12} />
              </button>
            </span>
          )}
          <button
            type="button"
            onMouseDown={keepFocus}
            onClick={() => remove(i)}
            className="shrink-0 opacity-0 transition-opacity group-hover:opacity-60 hover:!opacity-100"
            title="Remove"
            aria-label={`Remove ${linkLabel(l)}`}
          >
            <Icon name="X" size={12} />
          </button>
        </span>
      ))}

      {/* The row that does not exist yet.
          It used to be two borderless inputs side by side, so an empty Links block
          rendered as the words "name" and "https://" floating with nothing between
          them — which reads as a broken layout rather than as an empty field. A
          dashed frame is the app's existing way of saying "this is not a row yet"
          (see `.adder` in global.css), and it gives the two placeholders something
          to sit in. */}
      <span
        className="rounded-inline flex min-w-0 items-center gap-1.5 border border-dashed px-1.5 py-0.5"
        style={{ borderColor: 'var(--border)' }}
      >
        <Icon name="Plus" size={12} className="shrink-0" style={{ color: 'var(--faint)' }} />
        <input
          value={draft.label}
          onChange={(e) => setDraft({ ...draft, label: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitDraft()
            if (e.key === 'Escape') setDraft({ label: '', url: '' })
          }}
          placeholder="name"
          className="text-body min-w-0 flex-[2] bg-transparent outline-none"
        />
        <input
          value={draft.url}
          onChange={(e) => setDraft({ ...draft, url: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitDraft()
            if (e.key === 'Escape') setDraft({ label: '', url: '' })
          }}
          onBlur={commitDraft}
          placeholder="https://"
          className="min-w-0 flex-[3] bg-transparent outline-none"
          style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-label)' }}
        />
      </span>
    </div>
  )
}
